import React, {useEffect, useState} from "react";
import {t} from "../common/LocalizationProvider";
import Box from "@material-ui/core/Box";
import CircularProgress from "@material-ui/core/CircularProgress";
import Typography from "@material-ui/core/Typography";
import {makeStyles} from "@material-ui/core/styles";
import {cyberStyles} from "../cssShared/cyberTheme";


const NOMINATIM_API_URL = 'https://nominatim.openstreetmap.org/reverse';
const NOMINATIM_USER_AGENT = 'YourAppOrServiceID/1.0 (YourContactInfo)';

const useStyles = makeStyles((theme) => ({
    ...cyberStyles(theme),
    result: {
        fontWeight: 'normal',
        fontSize: 'inherit',
        wordBreak: 'break-word',
    },
    addressContainer: {
        display: 'flex',
        alignItems: 'center',
    },
    loadingSpinner: {
        marginRight: theme.spacing(1),
    },
    errorText: {
        color: theme.palette.error.main,
        fontSize: 'inherit',
    }
}));

const AddressDisplay = ({ latitude, longitude, mapType }) => {
    const classes = useStyles();
    const [address, setAddress] = useState(t('GettingAddress'));
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (latitude == null || longitude == null) {
            setError(t('errorInvalidCoordinates'));
            setAddress(null);
            setIsLoading(false);
            return;
        }

        const controller = new AbortController();
        const { signal } = controller;

        const fetchAddress = async () => {
            setIsLoading(true);
            setError(null);
            setAddress(null);

            const url = `${NOMINATIM_API_URL}?lat=${latitude}&lon=${longitude}&format=json&addressdetails=0`;

            try {
                const response = await fetch(url, {
                    headers: { 'User-Agent': NOMINATIM_USER_AGENT },
                    signal,
                });
                if (!response.ok) {
                    throw new Error(`HTTP error! status: ${response.status}`);
                }
                const data = await response.json();
                if (data.error) {
                    throw new Error(data.error);
                }
                if (data.display_name) {
                    setAddress(data.display_name);
                } else {
                    setAddress(t('addressNotFound'));
                }

            }
            catch (err) {
                if (err.name === 'AbortError') {
                    console.log('Address fetch aborted');
                } else {
                    console.error("Failed to fetch address:", err);
                    setError(t('errorFetchingAddress'));
                }
            }
            finally {
                if (signal.aborted === false) {
                    setIsLoading(false);
                }
            }
        };

        const fetchGoogleAddress = async () => {
            if (!window.google || !window.google.maps || !latitude || !longitude) return;
            try {
                const response = await fetch(`/api/googleAddress?lat=${latitude}&lng=${longitude}`);
                const data = await response.json();
                if (data && data.Address)
                    setAddress(data.Address);
                else
                    setAddress(t("NoAddress"))
            } catch (error) {
                console.error("Secure Geocoding failed", error);
                setAddress(t("errorMessage"))
            }

            //this is old "get address" from google map using get request:
            // const geocoder = new window.google.maps.Geocoder();
            // const latlng = {
            //     lat: parseFloat(latitude),
            //     lng: parseFloat(longitude),
            // };
            // geocoder.geocode({ location: latlng }, (results, status) => {
            //     if (status === 'OK' && results[0]) {
            //         const components = results[0].address_components;
            //         // Skip the first component (usually the Plus Code)
            //         const filteredComponents = components.slice(1);
            //         // Extract only the `long_name` fields from remaining components
            //         const parts = filteredComponents.map(comp => comp.long_name);
            //         // Remove duplicates (e.g. Salah Bey appears twice)
            //         const uniqueParts = [...new Set(parts)];
            //         // Join the parts into a full address
            //         const formattedAddress = uniqueParts.join(', ');
            //         setAddress(formattedAddress || t('NoAddress'));
            //     }
            //     else {
            //         console.error('Geocoder failed due to:', status);
            //         setAddress(t('NoAddress'));
            //     }
            // });
        }

        if (mapType === 'googleMap')
            fetchGoogleAddress();
        else
            fetchAddress();

        return () => {
            controller.abort();
        };
    }, [latitude, longitude]);

    return (
        <Box className={classes.addressContainer}>
            {isLoading && (
                <>
                    <CircularProgress size={14} className={classes.loadingSpinner} />
                    <Typography variant="body2" className={classes.cancelButtonRemoveDialog}>{t('addressLoading')}</Typography>
                </>
            )}
            {error && <Typography variant="body2" className={classes.errorText}>{error}</Typography>}
            {!isLoading && !error && address && <Typography variant="body2" className={classes.cancelButtonRemoveDialog}>{address}</Typography>}
            {!isLoading && !error && !address && <Typography variant="body2" className={classes.cancelButtonRemoveDialog}>{t('addressNotAvailable')}</Typography>}
        </Box>
    );
};

export default AddressDisplay;
